<template>
  <div>
    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)"><div class="notice__body">{{ error }}</div></div>

    <div class="card">
      <h2 class="card__title">{{ t("editor.inviteTester") }}</h2>
      <p class="card__sub">{{ t("editor.testersLede", { max: MAX_DAYS }) }}</p>
      <div class="grid grid--2" style="margin-top: var(--space-4)">
        <div class="field">
          <label class="field__label" for="t-inst">{{ t("testers.installation") }} <span class="req">*</span></label>
          <select id="t-inst" v-model="form.installation_id" class="select">
            <option value="" disabled>{{ t("testers.pickInstallation") }}</option>
            <option v-for="i in installations" :key="i.id" :value="i.id">{{ i.name }} ({{ i.code }})</option>
          </select>
        </div>
        <div class="field">
          <label class="field__label" for="t-acc">{{ t("testers.accountRef") }} <span class="req">*</span></label>
          <input id="t-acc" v-model.trim="form.account_ref" class="input input--mono" maxlength="64" />
          <span class="field__hint">{{ t("testers.accountRefHint") }}</span>
        </div>
        <div class="field">
          <label class="field__label" for="t-email">{{ t("testers.email") }}</label>
          <input id="t-email" v-model.trim="form.email" class="input" type="email" />
          <span class="field__hint">{{ t("testers.emailHint") }}</span>
        </div>
        <div class="field">
          <label class="field__label" for="t-exp">{{ t("editor.expires") }} <span class="req">*</span></label>
          <input id="t-exp" v-model="form.expires_on" class="input" type="date" :min="minDate" :max="maxDate" />
          <span class="field__hint">{{ t("testers.expiresHint", { max: MAX_DAYS }) }}</span>
        </div>
      </div>
      <button class="btn" :disabled="busy || !valid" @click="invite">
        <span v-if="busy" class="spinner" aria-hidden="true"></span>
        {{ t("testers.invite") }}
      </button>
    </div>

    <div class="card">
      <div class="card__head">
        <h2 class="card__title">{{ t("editor.testers") }}</h2>
        <button type="button" class="btn btn--secondary btn--sm" :disabled="loading" @click="load">{{ t("common.refresh") }}</button>
      </div>
      <div v-if="loading" class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>
      <p v-else-if="!testers.length" class="muted small">{{ t("testers.empty") }}</p>
      <div v-else class="table__wrap">
        <table class="table">
          <thead>
            <tr>
              <th>{{ t("testers.installation") }}</th>
              <th>{{ t("testers.accountRef") }}</th>
              <th>{{ t("testers.email") }}</th>
              <th>{{ t("common.status") }}</th>
              <th>{{ t("editor.expires") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tr in testers" :key="tr.id">
              <td class="small">{{ installationName(installations, tr.installation_id) }}</td>
              <td class="mono xs">{{ tr.account_ref }}</td>
              <td class="small">{{ tr.email || "—" }}</td>
              <td>
                <span :class="['badge', TESTER_BADGE[tr.status] || '']">{{ testerStatus(tr.status) }}</span>
                <div v-if="tr.confirmed_at" class="xs muted">{{ t("testers.confirmedAt", { when: formatDate(tr.confirmed_at, false) }) }}</div>
              </td>
              <td class="small muted">{{ formatDate(tr.expires_at, false) }}</td>
              <td>
                <button v-if="!['revoked', 'expired'].includes(tr.status)" type="button" class="btn btn--ghost btn--sm"
                        :disabled="busy" @click="revoke(tr)">
                  {{ t("editor.revoke") }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../../lib/api";
import { useErrorText, formatDate } from "../../lib/errors";
import { loadInstallations, installationName } from "../../lib/installations";

const props = defineProps({ appId: { type: String, required: true } });
const { t, te } = useI18n();
const errorText = useErrorText();

const MAX_DAYS = 90;
const DEFAULT_DAYS = 30;
const TESTER_BADGE = { active: "badge--success", pending: "badge--info", revoked: "badge--danger", expired: "" };

const isoDate = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
const minDate = isoDate(1);
const maxDate = isoDate(MAX_DAYS - 1);

const testers = ref([]);
const installations = ref([]);
const loading = ref(true);
const busy = ref(false);
const error = ref("");
const form = reactive({ installation_id: "", account_ref: "", email: "", expires_on: isoDate(DEFAULT_DAYS) });

const valid = computed(
  () => !!form.installation_id && !!form.account_ref && !!form.expires_on && form.expires_on >= minDate && form.expires_on <= maxDate
);

function testerStatus(status) {
  return te(`testers.status.${status}`) ? t(`testers.status.${status}`) : status;
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const [rows, list] = await Promise.all([api.testers(props.appId), loadInstallations()]);
    testers.value = rows || [];
    installations.value = list;
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}

async function run(fn) {
  busy.value = true;
  error.value = "";
  try {
    await fn();
    await load();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}

const invite = () => run(async () => {
  await api.inviteTester(props.appId, {
    installation_id: form.installation_id,
    account_ref: form.account_ref,
    email: form.email || null,
    // End of the chosen day in UTC, so the hub's ≤ 90-day check sees the date the developer picked.
    expires_at: `${form.expires_on}T23:59:59.000Z`
  });
  form.account_ref = "";
  form.email = "";
});

const revoke = (tr) => {
  if (!window.confirm(t("testers.revokeConfirm", { ref: tr.account_ref }))) return;
  return run(() => api.revokeTester(props.appId, tr.id));
};

onMounted(load);
</script>
